import { supabase } from '../supabaseClient';
import {
  EVENTS_PAGE_SIZE,
  normalizeRpcEventRow,
  defaultRpcMinDateIso,
} from '../utils/eventFeedModel';
import { MAX_DISPLAY_GRACE_DAYS } from '../utils/eventCutoff';
import { VENUE_FILTERS } from '../utils/venueFilters';

export async function fetchDistinctGenres() {
  const { data, error } = await supabase.rpc('get_distinct_genres');
  if (error) throw error;
  return data ?? [];
}

export function buildFeedRpcParams({
  offset = 0,
  sortBy = 'popular',
  genreFilter = 'All Genres',
  debouncedSearch = '',
  venueTierFilter = 'all',
  computeRpcMinDate,
  computeRpcMaxDate,
  userId = null,
}) {
  const rpcName = sortBy === 'popular' ? 'get_popular_events' : 'get_events_by_date';
  const venueTiers = (() => {
    if (venueTierFilter === 'all') return null;
    const filter = VENUE_FILTERS.find((f) => f.value === venueTierFilter);
    return filter?.tiers ?? null;
  })();
  const searchParam = debouncedSearch.trim() === '' ? null : debouncedSearch.trim();
  const minDate =
    typeof computeRpcMinDate === 'function' ? computeRpcMinDate() : defaultRpcMinDateIso();

  return {
    rpcName,
    params: {
      p_limit: EVENTS_PAGE_SIZE,
      p_offset: offset,
      p_genre: genreFilter === 'All Genres' ? null : genreFilter,
      p_min_date: minDate,
      p_max_date: typeof computeRpcMaxDate === 'function' ? computeRpcMaxDate() : null,
      p_venue_tiers: venueTiers,
      p_search: searchParam,
      p_current_user_id: userId ?? null,
    },
  };
}

export async function fetchEventsFeedPage({ rpcName, params }) {
  const { data, error } = await supabase.rpc(rpcName, params);
  if (error) throw error;
  return (data ?? []).map(normalizeRpcEventRow);
}

/**
 * Every upcoming festival, soonest first — the Events page's pinned Festivals section.
 *
 * This does NOT come from the paginated feed, and cannot. Festivals are rare and often
 * months out: on 2026-09-22 the feed held 2,260 upcoming events of which 10 were
 * festivals, and the FIRST festival by date was row 214 — fourteen pages of infinite
 * scroll before one appears. Sorting the loaded pages would surface nothing, so the
 * section asks for festivals directly.
 *
 * It is the same RPC the feed uses, with the Festivals tier. That predicate means
 * "this IS a festival" (`e.is_festival`), not "this is at a festival-tier venue", so
 * the section inherits every gate the feed already applies for free: day/pass variants
 * folded into a parent, and the festival_ready manual gate. A festival the feed will
 * not show is one whose event page says "Event not found" — it must not be pinned to
 * the top of the page.
 */
export async function fetchUpcomingFestivals() {
  const { data, error } = await supabase.rpc('get_events_by_date', {
    p_limit: 40,
    p_offset: 0,
    p_genre: null,
    p_min_date: defaultRpcMinDateIso(),
    p_max_date: null,
    p_venue_tiers: ['festival'],
    p_search: null,
    p_current_user_id: null,
  });
  if (error) throw error;
  // The tier predicate is an OR: a non-festival at a festival-tier venue also matches.
  return (data ?? []).filter((row) => row.is_festival).map(normalizeRpcEventRow);
}

/**
 * Upcoming events the promoter has cancelled — ids only, so every surface can drop them.
 *
 * This exists because nothing else can see them. The feed RPCs (get_popular_events,
 * get_events_by_date) neither filter on `events.status` nor return it, so a cancelled
 * show is indistinguishable from a live one by the time it reaches the client. Measured
 * 2026-09-22: 42 upcoming cancelled events were being served, and two of them —
 * "Elefante" and "Lily Allen Performs West End Girl" — were on the first screen of the
 * feed.
 *
 * The right fix is one predicate in each RPC. That is a migration touching every feed
 * query, which is not a thing to ship the week of a QA weekend, so this closes it from
 * the client meanwhile: one small query (tens of ids), cached, and the surfaces filter
 * against it. Delete this the day the RPCs learn about status.
 */
export async function fetchCancelledEventIds() {
  const since = new Date();
  since.setDate(since.getDate() - MAX_DISPLAY_GRACE_DAYS);

  const { data, error } = await supabase
    .from('events')
    .select('event_id')
    .eq('status', 'cancelled')
    .gte('event_date', since.toISOString().slice(0, 10));
  if (error) throw error;
  return (data ?? []).map((row) => row.event_id);
}

// A desktop browser shows a dozen-plus phone-size cards across the screen, so it gets more of them (Sam, 2026-10-23).
const trendingLimit = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
    ? 15
    : 6;

export async function fetchTrendingEvents(userId) {
  const { data, error } = await supabase.rpc('get_popular_events', {
    p_limit: trendingLimit(),
    p_offset: 0,
    p_genre: null,
    // The feed's window, not now: a show stays trending until it is over (ends_at, checked by the RPC), since
    // the minutes around the start are when people join (Sam, 2026-11-04). With now it vanished at the first song.
    p_min_date: defaultRpcMinDateIso(),
    p_max_date: null,
    p_venue_tiers: null,
    p_search: null,
    p_current_user_id: userId ?? null,
  });
  if (error) throw error;
  return (data ?? []).map(normalizeRpcEventRow);
}

export async function fetchEventSearchPage({ query, offset = 0, userId = null }) {
  const { data, error } = await supabase.rpc('get_popular_events', {
    p_limit: EVENTS_PAGE_SIZE,
    p_offset: offset,
    p_genre: null,
    p_min_date: defaultRpcMinDateIso(),
    p_max_date: null,
    p_venue_tiers: null,
    p_search: query.trim() || null,
    p_current_user_id: userId ?? null,
  });
  if (error) throw error;
  return (data ?? []).map(normalizeRpcEventRow);
}

export async function fetchEventDetail(eventId) {
  const { data, error } = await supabase
    .from('events')
    .select('*, venues(*)')
    .eq('event_id', eventId)
    .maybeSingle();
  if (error) throw error;
  if (data && String(data.event_type || '').toLowerCase() === 'festival' && !data.festival_ready) {
    return null;
  }
  return data;
}

export async function fetchEventRsvps(eventId) {
  const { data, error } = await supabase
    .from('rsvps')
    .select('id, user_id, status, created_at, profiles(display_name, profile_photo_url)')
    .eq('event_id', eventId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function fetchEventForCutoff(eventId) {
  const { data, error } = await supabase
    .from('events')
    .select('event_id, event_datetime, event_date, ends_at, multi_day, venues(venue_tier)')
    .eq('event_id', eventId)
    .maybeSingle();
  if (error) throw error;
  return data;
}
